/** @jsxImportSource @emotion/react */
import { Global } from '@emotion/react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { globalStyles } from './styles/globalStyles'
import { LoadingProvider, useLoading } from './hooks/useLoading'
import { Header } from './components/Layout/Header'
import { Footer } from './components/Layout/Footer'
import { Loader } from './components/UI/Loader'
import { BackToTop } from './components/UI/BackToTop'
import { ScrollToTop } from './components/UI/ScrollToTop'
import { ChatWidget } from './components/Chat/ChatWidget'
import { HomePage } from './pages/HomePage'

const FAQPage = lazy(() => import('./pages/FAQPage').then(module => ({ default: module.FAQPage })))
const SupportPage = lazy(() => import('./pages/SupportPage').then(module => ({ default: module.SupportPage })))
const RetirementPlannerPage = lazy(() => import('./pages/RetirementPlannerPage').then(module => ({ default: module.RetirementPlannerPage })))
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage').then(module => ({ default: module.TermsOfServicePage })))
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage').then(module => ({ default: module.PrivacyPolicyPage })))
const ExecutionMetricsPage = lazy(() => import('./pages/ExecutionMetricsPage').then(module => ({ default: module.ExecutionMetricsPage })))
const PerformanceMetricsPage = lazy(() => import('./pages/PerformanceMetricsPage').then(module => ({ default: module.PerformanceMetricsPage })))
const PerformanceMetricsGlossaryPage = lazy(() => import('./pages/PerformanceMetricsGlossaryPage').then(module => ({ default: module.PerformanceMetricsGlossaryPage })))
const SuccessPage = lazy(() => import('./pages/SuccessPage').then(module => ({ default: module.SuccessPage })))
const VideosPage = lazy(() => import('./pages/VideosPage').then(module => ({ default: module.VideosPage })))
const ArticlesPage = lazy(() => import('./pages/ArticlesPage').then(module => ({ default: module.ArticlesPage })))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then(module => ({ default: module.NotFoundPage })))
const RedirectPage = lazy(() => import('./pages/RedirectPage').then(module => ({ default: module.RedirectPage })))

function AppContent() {
  const { loading } = useLoading()

  return (
    <Router>
      <Global styles={globalStyles} />
      <ScrollToTop />
      
      <Header />
      
      <div id="main">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/faq" element={<Suspense fallback={<Loader show={true} />}><FAQPage /></Suspense>} />
          <Route path="/support" element={<Suspense fallback={<Loader show={true} />}><SupportPage /></Suspense>} />
          <Route path="/retirement-planner" element={<Suspense fallback={<Loader show={true} />}><RetirementPlannerPage /></Suspense>} />
          <Route path="/terms-of-service" element={<Suspense fallback={<Loader show={true} />}><TermsOfServicePage /></Suspense>} />
          <Route path="/privacy-policy" element={<Suspense fallback={<Loader show={true} />}><PrivacyPolicyPage /></Suspense>} />
          <Route path="/execution-metrics/:strategyName" element={<Suspense fallback={<Loader show={true} />}><ExecutionMetricsPage /></Suspense>} />
          <Route path="/performance-metrics/:strategyName" element={<Suspense fallback={<Loader show={true} />}><PerformanceMetricsPage /></Suspense>} />
          <Route path="/performance-metrics/glossary" element={<Suspense fallback={<Loader show={true} />}><PerformanceMetricsGlossaryPage /></Suspense>} />
          <Route path="/success" element={<Suspense fallback={<Loader show={true} />}><SuccessPage /></Suspense>} />
          <Route path="/videos" element={<Suspense fallback={<Loader show={true} />}><VideosPage /></Suspense>} />
          <Route path="/articles" element={<Suspense fallback={<Loader show={true} />}><ArticlesPage /></Suspense>} />
          <Route path="/redirect" element={<Suspense fallback={<Loader show={true} />}><RedirectPage /></Suspense>} />
          <Route path="*" element={<Suspense fallback={<Loader show={true} />}><NotFoundPage /></Suspense>} />
        </Routes>
      </div>
      <Footer />

      <BackToTop />

      <ChatWidget />

      <Loader show={loading} />
    </Router>
  )
}

function App() {
  return (
    <LoadingProvider>
      <AppContent />
    </LoadingProvider>
  )
}

export default App